import { css } from 'styled-components';

/**
 * @constant
 * @description Application color palette constants for consistent theming across the application.
 * Contains primary, secondary, tertiary colors and their variants.
 *
 * @type {Object}
 * @property {string} PRIMARY - Main blue color (#2196f3)
 * @property {string} PRIMARY_DARK - Dark blue variant (#1769aa)
 * @property {string} PRIMARY_LIGHT - Light blue variant (#002047)
 * @property {string} SECONDARY - Main pink/red color (#f50057)
 * @property {string} SECONDARY_DARK - Dark pink variant (#ab003c)
 * @property {string} SECONDARY_LIGHT - Light pink variant (#f73378)
 * @property {string} ORANGE_DARK - Dark orange color (#f78035)
 * @property {string} ORANGE_LIGHT - Light orange color (#FAA637)
 * @property {string} TERTIARY - Main cyan color (#3A82A7)
 * @property {string} TERTIARY_LIGHT - Light cyan variant (#92abbd)
 * @property {string} DANGER - Error/danger color (#d32f2f)
 * @property {string} GREY_TEXT_COLOR - Grey text color (#707070)
 *
 * @example
 * import { COLORS } from './Styles/Constants';
 *
 * const Button = styled.button`
 *   background-color: ${COLORS.PRIMARY};
 *   color: white;
 * `;
 */
export const COLORS = {
    // BLUE
    PRIMARY: '#2196f3',
    PRIMARY_DARK: '#1769aa',
    PRIMARY_LIGHT: '#002047',

    // ORANGE
    SECONDARY: '#f50057',
    SECONDARY_DARK: '#ab003c',
    SECONDARY_LIGHT: '#f73378',

    ORANGE_DARK: '#f78035',
    ORANGE_LIGHT: '#FAA637',

    //CYAN
    TERTIARY: '#3A82A7',
    TERTIARY_LIGHT: '#92abbd',

    //RED
    DANGER: '#d32f2f',

    GREY_TEXT_COLOR: '#707070'
};

/**
 * @constant
 * @description Breakpoint pixel values for responsive design.
 * Defines screen width thresholds for different device types.
 *
 * @type {Object}
 * @property {number} DISPLAY - Large display screens (1750px)
 * @property {number} DESKTOP - Desktop screens (1450px)
 * @property {number} LAPTOP - Laptop screens (1278px)
 * @property {number} TABLET - Tablet screens (960px)
 * @property {number} PHABLET - Large mobile/phablet screens (600px)
 * @property {number} MOBILE - Mobile screens (450px)
 *
 * @see BREAKPOINTS - Media query breakpoints
 */
export const BREAKPOINTS_VALUE = {
    DISPLAY: 1750,
    DESKTOP: 1450, // x_large
    LAPTOP: 1278, // large
    TABLET: 960, // medium
    PHABLET: 600, // small
    MOBILE: 450 // x_small
};

/**
 * @constant
 * @description Media query breakpoints for responsive design.
 * Contains both minimum width (A_*) and maximum width breakpoints.
 *
 * @type {Object}
 * @property {string} A_DESKTOP - Min-width desktop breakpoint (1450px)
 * @property {string} A_LAPTOP - Min-width laptop breakpoint (1278px)
 * @property {string} A_TABLET - Min-width tablet breakpoint (960px)
 * @property {string} A_PHABLET - Min-width phablet breakpoint (600px)
 * @property {string} A_MOBILE - Min-width mobile breakpoint (450px)
 * @property {string} DISPLAY - Max-width display breakpoint (1749px)
 * @property {string} DESKTOP - Max-width desktop breakpoint (1449px)
 * @property {string} LAPTOP - Max-width laptop breakpoint (1277px)
 * @property {string} TABLET - Max-width tablet breakpoint (959px)
 * @property {string} PHABLET - Max-width phablet breakpoint (599px)
 * @property {string} MOBILE - Max-width mobile breakpoint (449px)
 *
 * @example
 * import { BREAKPOINTS } from './Styles/Constants';
 *
 * const ResponsiveDiv = styled.div`
 *   width: 100%;
 *   @media (max-width: ${BREAKPOINTS.TABLET}) {
 *     width: 50%;
 *   }
 * `;
 */
export const BREAKPOINTS = {
    A_DESKTOP: `${BREAKPOINTS_VALUE.DESKTOP}px`,
    A_LAPTOP: `${BREAKPOINTS_VALUE.LAPTOP}px`,
    A_TABLET: `${BREAKPOINTS_VALUE.TABLET}px`,
    A_PHABLET: `${BREAKPOINTS_VALUE.PHABLET}px`,
    A_MOBILE: `${BREAKPOINTS_VALUE.MOBILE}px`,

    DISPLAY: `${BREAKPOINTS_VALUE.DISPLAY - 1}px`,
    DESKTOP: `${BREAKPOINTS_VALUE.DESKTOP - 1}px`, // x_large
    LAPTOP: `${BREAKPOINTS_VALUE.LAPTOP - 1}px`, // large
    TABLET: `${BREAKPOINTS_VALUE.TABLET - 1}px`, // medium
    PHABLET: `${BREAKPOINTS_VALUE.PHABLET - 1}px`, // small
    MOBILE: `${BREAKPOINTS_VALUE.MOBILE - 1}px` // x_small
};

/**
 * @constant
 * @description Font family constants for typography.
 * Currently contains commented-out font definitions for future use.
 *
 * @type {Object}
 * @deprecated Most font definitions are commented out
 *
 * @example
 * // When fonts are defined:
 * const Text = styled.p`
 *   font-family: ${FONTS.PRIMARY};
 * `;
 */
export const FONTS = {
    // PRIMARY         : "Gotham Book",
    // PRIMARY_BOLD    : "Gotham Bold",
    // PRIMARY_LIGHT   : "Gotham Thin",
    // PRIMARY_MEDIUM  : "Gotham Medium",
    // PRIMARY_ITALIC  : "Gotham Book Italic",
    // SECONDARY         : "PolarisCondensed-Book",
    // SECONDARY_MEDIUM  : "PolarisCondensed-Medium",
    // SECONDARY_HEAVY   : "PolarisCondensed-Heavy"
};

export const responsive = Object.keys(BREAKPOINTS).reduce((acc, label) => {
    acc[label] = (...args) => css`
        @media (max-width: ${BREAKPOINTS[label]}) {
            ${css(...args)}
        }
    `;
    return acc;
}, {});
